import { ProgramStatus, ProgramType, ProgramWithDetail, UpdateProgram } from "@humanex/interfaces";
import { Box, Button, ImageInput, Input, Select, TiptapEditor } from "@humanex/ui/components";
import { toast, useI18n } from "@humanex/ui/libs";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useCreateEditRevision, useUpdateProgram, useUploadImage } from "@/hooks/queries";

interface EditProgramPageProps {
  orgId: string;
  programId: string;
  program: ProgramWithDetail;
}

interface FormData {
  title: string;
  description: string;
  banner: string;
  type: ProgramType;
  country: string;
  location: string;
  quota: number;
  requirements: string;
  benefits: string;
  costVisible: boolean;
  depositAmount: number;
  deadline: string;
}

const PROGRAM_TYPE_OPTIONS = [
  { value: ProgramType.DU_HOC, label: "Du học" },
  { value: ProgramType.VIEC_LAM, label: "Việc làm trong nước" },
  { value: ProgramType.XUAT_KHAU, label: "Xuất khẩu lao động" },
  { value: ProgramType.HOC_NGHE, label: "Học nghề" }
];

const DRAFT_LIKE = [ProgramStatus.DRAFT, ProgramStatus.REJECTED];

export function EditProgramPage({ orgId, programId, program }: EditProgramPageProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDraftLike = DRAFT_LIKE.includes(program.status);

  const { mutateAsync: updateProgram } = useUpdateProgram();
  const { mutateAsync: createEditRevision } = useCreateEditRevision();
  const { mutateAsync: uploadImage } = useUploadImage();

  const form = useForm<FormData>({
    defaultValues: {
      title: program.title ?? "",
      description: program.description ?? "",
      banner: program.banner ?? "",
      type: program.type ?? ProgramType.DU_HOC,
      country: program.country ?? "",
      location: program.location ?? "",
      quota: program.quota ?? 1,
      requirements: program.requirements ?? "",
      benefits: program.benefits ?? "",
      costVisible: program.costVisible ?? false,
      depositAmount: program.depositAmount ?? 0,
      deadline: program.deadline ? new Date(program.deadline).toISOString().slice(0, 16) : ""
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        const data: UpdateProgram = {
          title: value.title.trim(),
          description: value.description.trim(),
          banner: value.banner.trim() || null,
          type: value.type,
          country: value.country.trim() || null,
          location: value.location.trim() || null,
          quota: value.quota,
          requirements: value.requirements.trim() || null,
          benefits: value.benefits.trim() || null,
          costVisible: value.costVisible,
          depositAmount: value.depositAmount,
          deadline: value.deadline ? new Date(value.deadline) : null
        };

        if (isDraftLike) {
          await updateProgram({ id: programId, data });
        } else {
          await createEditRevision({ id: programId, data });
        }

        toast.success("Chương trình đã được cập nhật!");
        navigate({ to: "/org/$orgId/program/$programId", params: { orgId, programId } });
      } catch (error) {
        console.error("Error updating program:", error);
        toast.error("Có lỗi xảy ra khi cập nhật chương trình");
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  return (
    <div className="py-4 md:py-6 lg:py-8 px-0 sm:px-2 md:px-4 lg:px-8 space-y-6">
      <div className="space-y-4 sm:space-y-6 max-w-4xl mx-auto">
        <div className="px-4 md:px-6">
          <p className="text-responsive-xl font-normal text-text-primary mb-2">Chỉnh sửa chương trình</p>
          <p className="text-responsive-base text-text-secondary">{program.title}</p>
        </div>

        {!isDraftLike && (
          <div className="mx-4 md:mx-6 p-4 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
            Chương trình đang được công khai. Thay đổi sẽ tạo một phiên bản mới để duyệt — bản hiện tại vẫn
            hiển thị cho đến khi phiên bản mới được duyệt và đăng.
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          {/* Basic Info */}
          <div className="px-4 md:px-6 py-2">
            <p className="text-text-secondary text-responsive-lg font-normal">{t("org.edit.basicInfo")}</p>
          </div>
          <Box className="space-y-6 mb-6">
            <form.Field
              name="title"
              validators={{ onChange: ({ value }) => (!value?.trim() ? "Tiêu đề là bắt buộc" : undefined) }}
            >
              {(field) => (
                <Input
                  label="Tiêu đề chương trình"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Nhập tiêu đề chương trình"
                  required
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            <form.Field
              name="type"
              validators={{ onChange: ({ value }) => (!value ? "Loại chương trình là bắt buộc" : undefined) }}
            >
              {(field) => (
                <Select
                  label="Loại chương trình"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value as ProgramType)}
                  options={PROGRAM_TYPE_OPTIONS}
                  required
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            <form.Field
              name="description"
              validators={{ onChange: ({ value }) => (!value?.trim() ? "Mô tả là bắt buộc" : undefined) }}
            >
              {(field) => (
                <TiptapEditor
                  label="Mô tả chương trình"
                  placeholder="Nhập mô tả chi tiết về chương trình"
                  required
                  error={field.state.meta.errors[0] || undefined}
                  value={field.state.value}
                  onChange={field.handleChange}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            <form.Field name="banner">
              {(field) => (
                <ImageInput
                  value={field.state.value}
                  onChange={field.handleChange}
                  uploadFn={uploadImage}
                  label="Banner"
                  previewSize="w-full max-w-md h-48"
                  disabled={isSubmitting}
                  toastSuccess={toast.success}
                  toastError={toast.error}
                  successMessage={t("org.edit.avatarUploadSuccess")}
                  errorMessage={t("org.edit.avatarUploadError")}
                  invalidImageTypeMessage={t("org.edit.errors.invalidImageType")}
                  fileTooLargeMessage={t("profile.basicInfo.fileTooLarge")}
                  alt="Banner preview"
                />
              )}
            </form.Field>
          </Box>

          {/* Location & Details */}
          <div className="px-4 md:px-6 py-2">
            <p className="text-text-secondary text-responsive-lg font-normal">ĐỊA ĐIỂM & CHI TIẾT</p>
          </div>
          <Box className="space-y-6 mb-6">
            <form.Field name="country">
              {(field) => (
                <Input
                  label="Quốc gia (tuỳ chọn)"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Ví dụ: Nhật Bản, Hàn Quốc"
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            <form.Field name="location">
              {(field) => (
                <Input
                  label="Địa điểm cụ thể (tuỳ chọn)"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Ví dụ: Tokyo, Osaka"
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            <form.Field
              name="quota"
              validators={{
                onChange: ({ value }) => (!value || value < 1 ? "Chỉ tiêu phải ít nhất là 1" : undefined)
              }}
            >
              {(field) => (
                <Input
                  label="Chỉ tiêu tuyển dụng"
                  type="number"
                  value={field.state.value.toString()}
                  onChange={(e) => field.handleChange(parseInt(e.target.value) || 1)}
                  placeholder="Nhập số lượng chỉ tiêu"
                  required
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            <form.Field name="deadline">
              {(field) => (
                <Input
                  label="Hạn nộp hồ sơ (tuỳ chọn)"
                  type="datetime-local"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>
          </Box>

          {/* Requirements & Benefits */}
          <div className="px-4 md:px-6 py-2">
            <p className="text-text-secondary text-responsive-lg font-normal">YÊU CẦU & QUYỀN LỢI</p>
          </div>
          <Box className="space-y-6 mb-6">
            <form.Field name="requirements">
              {(field) => (
                <TiptapEditor
                  label="Yêu cầu ứng viên (tuỳ chọn)"
                  placeholder="Mô tả các yêu cầu đối với ứng viên"
                  value={field.state.value}
                  onChange={field.handleChange}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            <form.Field name="benefits">
              {(field) => (
                <TiptapEditor
                  label="Quyền lợi ứng viên (tuỳ chọn)"
                  placeholder="Mô tả các quyền lợi ứng viên được hưởng"
                  value={field.state.value}
                  onChange={field.handleChange}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>
          </Box>

          {/* Deposit / Cost */}
          <div className="px-4 md:px-6 py-2">
            <p className="text-text-secondary text-responsive-lg font-normal">PHÍ & THANH TOÁN</p>
          </div>
          <Box className="space-y-6 mb-6">
            <form.Field
              name="depositAmount"
              validators={{
                onChange: ({ value }) => (value < 0 ? "Phí đặt cọc không được âm" : undefined)
              }}
            >
              {(field) => (
                <Input
                  label="Phí đặt cọc (VND)"
                  type="number"
                  value={field.state.value.toString()}
                  onChange={(e) => field.handleChange(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  error={field.state.meta.errors[0] || undefined}
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            <form.Field name="costVisible">
              {(field) => (
                <div className="flex items-center gap-3">
                  <input
                    id="costVisible"
                    type="checkbox"
                    checked={field.state.value}
                    onChange={(e) => field.handleChange(e.target.checked)}
                    disabled={isSubmitting}
                    className="w-4 h-4 rounded border border-border accent-text-primary"
                  />
                  <label htmlFor="costVisible" className="text-sm text-text-primary">
                    Hiển thị chi phí công khai
                  </label>
                </div>
              )}
            </form.Field>
          </Box>

          <div className="flex gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate({ to: "/org/$orgId/program/$programId", params: { orgId, programId } })}
              disabled={isSubmitting}
            >
              Hủy
            </Button>
            <Button type="submit" variant="default" disabled={isSubmitting}>
              {isSubmitting ? "Đang lưu..." : "Lưu thay đổi"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
